import type {ReactElement} from 'react';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {getNextCodeIndex, getPreviousCodeIndex} from '@Components/qr-code-generator/qr-code-generator.library';
import {useIsCompactQRTool} from '@Components/qr-code-generator/qr-code-generator-hooks';
import styles from './qr-code-carousel.module.scss';
import type {QRCodeCarouselProps} from './qr-code-carousel.types';

export function QRCodeCarousel({controls, children}: QRCodeCarouselProps): ReactElement {
  const isCompact = useIsCompactQRTool();

  const renderArrow = (isBack: boolean): ReactElement | null => {
    if (controls === null) {
      return null;
    }
    const stepTo = isBack ? getPreviousCodeIndex(controls.visibleIndex, controls.codeCount) : getNextCodeIndex(controls.visibleIndex, controls.codeCount);

    return (
      <Icon
        icon={isBack ? 'icon-caret-left' : 'icon-caret-right'}
        size={isCompact ? IconSize.SIZE_ICON_16 : IconSize.SIZE_ICON_20}
        isUsedAsButton
        ariaLabel={window.i18next.t(isBack ? 'pmwjs_qr_generator_previous_code' : 'pmwjs_qr_generator_next_code')}
        className={`${styles.qrCodeCarouselArrow} ${isBack ? styles.qrCodeCarouselArrowBack : styles.qrCodeCarouselArrowForward}`}
        onClick={(): void => {
          controls.onSelectIndex(stepTo);
        }}
      />
    );
  };

  const renderDots = (): ReactElement | null => {
    if (controls === null) {
      return null;
    }
    return (
      <div className={styles.qrCodeCarouselDots}>
        {Array.from({length: controls.codeCount}, (_unusedValue, index): ReactElement => {
          return (
            <button
              key={index}
              type="button"
              className={`${styles.qrCodeCarouselDot} ${index === controls.visibleIndex ? styles.qrCodeCarouselDotActive : ''}`}
              aria-label={controls.getCodeAriaLabel(index)}
              aria-current={index === controls.visibleIndex}
              onClick={(): void => {
                controls.onSelectIndex(index);
              }}
            />
          );
        })}
      </div>
    );
  };

  return (
    <div className={styles.qrCodeCarousel}>
      <div className={styles.qrCodeCarouselViewport}>
        {children}
        {renderArrow(true)}
        {renderArrow(false)}
      </div>
      {renderDots()}
    </div>
  );
}
